/**
 * ContentHubItemNew Page
 * Provides a form to create a new content item
 */

import { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Check,
  Loader2,
  FileText,
  Hash,
  Image as ImageIcon,
  Table as TableIcon,
  Mail,
  Presentation,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { FluentCard } from '@/components/layout/page-header'
import { useToast } from '@/components/ui/use-toast'
import {
  CommonFieldsForm,
  TypeSpecificEditor,
  type CommonFieldsFormData,
  type TypeSpecificContent,
} from '@/components/content-hub'
import { useCreateContentItem } from '@/lib/hooks/use-content-hub'
import type {
  ContentItemType,
  CreateContentItemRequest,
  TextElementContent,
  DataPointContent,
  EmailSnippetContent,
} from '@/lib/api/types'

// ============================================
// Types & Constants
// ============================================

const TYPE_ICONS: Record<ContentItemType, React.ElementType> = {
  Text: FileText,
  DataPoint: Hash,
  Image: ImageIcon,
  Table: TableIcon,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const TYPE_COLORS: Record<ContentItemType, string> = {
  Text: 'from-blue-400 to-blue-600',
  DataPoint: 'from-green-400 to-green-600',
  Image: 'from-purple-400 to-purple-600',
  Table: 'from-amber-400 to-amber-600',
  EmailSnippet: 'from-rose-400 to-rose-600',
  SlideElement: 'from-orange-400 to-orange-600',
}

const TYPE_LABELS: Record<ContentItemType, string> = {
  Text: 'Text Element',
  DataPoint: 'Data Point',
  Image: 'Image',
  Table: 'Table',
  EmailSnippet: 'Email Snippet',
  SlideElement: 'Slide Element',
}

const TYPE_DESCRIPTIONS: Record<ContentItemType, string> = {
  Text: 'Reusable text snippets and paragraphs for documents',
  DataPoint: 'Dynamic data values and metrics that can be connected to sources',
  Image: 'Managed images with metadata for use in documents',
  Table: 'Structured data tables with formatting options',
  EmailSnippet: 'Reusable email components for Outlook templates',
  SlideElement: 'PowerPoint slides and presentation elements',
}

const VALID_TYPES: ContentItemType[] = ['Text', 'DataPoint', 'Image', 'Table', 'EmailSnippet', 'SlideElement']

// ============================================
// Invalid Type Component
// ============================================

interface InvalidTypeProps {
  type: string
}

function InvalidType({ type }: InvalidTypeProps) {
  const navigate = useNavigate()

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <Link
        to="/content-hub"
        className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Content Hub
      </Link>

      <FluentCard className="text-center py-12">
        <FileText className="h-12 w-12 mx-auto text-neutral-300 dark:text-neutral-600" />
        <h2 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
          Invalid Content Type
        </h2>
        <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
          The content type "{type}" is not recognized.
        </p>
        <Button onClick={() => navigate('/content-hub')} className="mt-4 rounded-full">
          Go to Content Hub
        </Button>
      </FluentCard>
    </div>
  )
}

// ============================================
// Helper Functions
// ============================================

function getDefaultTypeContent(type: ContentItemType): TypeSpecificContent {
  switch (type) {
    case 'Text':
      return {
        content: '',
        format: 'Plain' as const,
        language: undefined,
      }
    case 'DataPoint':
      return {
        valueType: 'Text' as const,
        currentValue: '',
        isLive: false,
      }
    case 'EmailSnippet':
      return {
        body: '',
        bodyFormat: 'Plain' as const,
        category: 'Body' as const,
        tone: 'Professional' as const,
      }
    default:
      return null
  }
}

function buildCreateRequest(
  type: ContentItemType,
  commonFields: CommonFieldsFormData,
  typeContent: TypeSpecificContent
): CreateContentItemRequest {
  const request: CreateContentItemRequest = {
    name: commonFields.name,
    description: commonFields.description || undefined,
    type,
    folderId: commonFields.folderId || undefined,
    tags: commonFields.tags,
  }

  // Add type-specific content based on content type
  switch (type) {
    case 'Text':
      if (typeContent) {
        const textContent = typeContent as Partial<TextElementContent>
        request.textContent = {
          content: textContent.content || '',
          format: textContent.format,
          language: textContent.language,
        }
      }
      break
    case 'DataPoint':
      if (typeContent) {
        const dpContent = typeContent as Partial<DataPointContent>
        request.dataPointContent = {
          valueType: dpContent.valueType || 'Text',
          currentValue: dpContent.currentValue || '',
          format: dpContent.format,
          unit: dpContent.unit,
          currencyCode: dpContent.currencyCode,
          decimalPlaces: dpContent.decimalPlaces,
          isLive: dpContent.isLive,
        }
      }
      break
    case 'EmailSnippet':
      if (typeContent) {
        const emailContent = typeContent as Partial<EmailSnippetContent>
        request.emailSnippetContent = {
          subject: emailContent.subject,
          body: emailContent.body || '',
          bodyFormat: emailContent.bodyFormat,
          signature: emailContent.signature,
          category: emailContent.category || 'Body',
          tone: emailContent.tone,
          language: emailContent.language,
        }
      }
      break
    // Other types can be added as editors are implemented
  }

  return request
}

// ============================================
// Main Component
// ============================================

export default function ContentHubItemNew() {
  const { type: typeParam } = useParams<{ type: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Normalize the type parameter (convert kebab-case to PascalCase)
  const normalizeType = (t: string): string => {
    const typeMap: Record<string, ContentItemType> = {
      'text': 'Text',
      'data-point': 'DataPoint',
      'datapoint': 'DataPoint',
      'image': 'Image',
      'table': 'Table',
      'email-snippet': 'EmailSnippet',
      'emailsnippet': 'EmailSnippet',
      'slide-element': 'SlideElement',
      'slideelement': 'SlideElement',
      'slide': 'SlideElement',
    }
    return typeMap[t.toLowerCase()] || t
  }

  const contentType = normalizeType(typeParam || '') as ContentItemType
  const isValidType = VALID_TYPES.includes(contentType)

  // Get default folder from URL params
  const defaultFolderId = searchParams.get('folderId')

  // Form state
  const [commonFields, setCommonFields] = useState<CommonFieldsFormData>({
    name: '',
    description: '',
    folderId: defaultFolderId,
    tags: [],
    isActive: true,
  })
  const [typeContent, setTypeContent] = useState<TypeSpecificContent>(() =>
    isValidType ? getDefaultTypeContent(contentType) : null
  )
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof CommonFieldsFormData, string>>>({})

  // Create mutation
  const createItem = useCreateContentItem({
    onSuccess: (newItem) => {
      setHasChanges(false)
      toast({
        title: 'Content created',
        description: 'Your content item has been created successfully.',
      })
      // Navigate to the new item detail page
      navigate(`/content-hub/item/${newItem.id}`)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create content item',
        variant: 'destructive',
      })
    },
  })

  // Track changes
  const handleCommonFieldsChange = useCallback((data: CommonFieldsFormData) => {
    setCommonFields(data)
    setHasChanges(true)
    // Clear name error when user starts typing
    if (data.name.trim() && errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }))
    }
  }, [errors.name])

  const handleTypeContentChange = useCallback((content: TypeSpecificContent) => {
    setTypeContent(content)
    setHasChanges(true)
  }, [])

  // Warn about unsaved changes when leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasChanges])

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CommonFieldsFormData, string>> = {}

    if (!commonFields.name.trim()) {
      newErrors.name = 'Name is required'
    }

    // Additional validation based on type
    if (contentType === 'Text') {
      const textContent = typeContent as Partial<TextElementContent>
      if (!textContent?.content?.trim()) {
        // Not required, but could add warning
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before saving',
        variant: 'destructive',
      })
      return
    }

    const request = buildCreateRequest(contentType, commonFields, typeContent)
    createItem.mutate(request)
  }

  // Handle cancel
  const handleCancel = () => {
    navigate('/content-hub')
  }

  // Invalid type
  if (!isValidType) {
    return <InvalidType type={typeParam || 'unknown'} />
  }

  const TypeIcon = TYPE_ICONS[contentType]
  const typeColor = TYPE_COLORS[contentType]
  const typeLabel = TYPE_LABELS[contentType]
  const typeDescription = TYPE_DESCRIPTIONS[contentType]

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link
        to="/content-hub"
        className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Content Hub
      </Link>

      {/* Page Header */}
      <div className="flex items-center gap-4">
        <div className={cn('p-3 rounded-2xl bg-gradient-to-br shadow-lg', typeColor)}>
          <TypeIcon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Create {typeLabel}
          </h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">{typeDescription}</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Type-Specific Editor */}
            <TypeSpecificEditor
              type={contentType}
              value={typeContent}
              onChange={handleTypeContentChange}
              disabled={createItem.isPending}
            />
          </div>

          {/* Sidebar - Common Fields */}
          <div className="space-y-0">
            <CommonFieldsForm
              value={commonFields}
              onChange={handleCommonFieldsChange}
              errors={errors}
              disabled={createItem.isPending}
            />

            {/* Content Type Info */}
            <FluentCard className="!mt-6 bg-neutral-50 dark:bg-neutral-800/50">
              <div className="flex items-center gap-3 mb-3">
                <div className={cn('p-2 rounded-xl bg-gradient-to-br', typeColor)}>
                  <TypeIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white">{typeLabel}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">Content Type</p>
                </div>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">{typeDescription}</p>
            </FluentCard>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={createItem.isPending}
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createItem.isPending}
            className="rounded-full gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 hover:bg-neutral-800 dark:hover:bg-neutral-100"
          >
            {createItem.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Create {typeLabel}
              </>
            )}
          </Button>
        </div>
      </form>

    </div>
  )
}
